// src/components/quizzes/TypingQuiz.js
//
// The "Typing Quiz" bonus round — rebuilt (Nov 2026) as WORD BLASTER.
// Same file, same name, same id (bonusRoundType "typingQuiz", game library
// "typingQuiz"), so every topic and game that uses it just gets the new one:
//
//     <TypingQuiz questions placeholderImage onComplete />
//     onComplete(score, { endedEarly })
//
// The old file was a copy of Multiple Choice with no typing in it at all —
// and at the end it tried to open ANOTHER bonus round with `topic.slug`,
// which a bonus round is never given, so it crashed.
//
// HOW IT PLAYS
//   ☄️ Meteors fall, each carrying a word from the topic (taken from the
//      right answers, so the round practises the topic's own vocabulary).
//   ⌨️ Type a word to lock on and blast it — the cannon swings round and
//      fires a laser. Letters light up as they're typed.
//   🔥 Every 5 words in a row raises the multiplier (up to ×3).
//   🌟 Gold meteors are worth +25 more; ⏰ blue ones add 5 seconds.
//   💥 A meteor that reaches the ground costs 5 points and the streak.
//   Words start short and get longer as the round goes on.
//
// Phones and tablets get a big on-screen keyboard (it can also be opened
// with the ⌨️ button on any screen); a real keyboard always works.
// Falling meteors use kit/Faller.js, so typing fast never makes them stutter.

import React, { useCallback, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { playGameSound } from "../../services/gameSounds";
import Faller from "./kit/Faller";
import BonusEndEarly from "./kit/BonusEndEarly";
import { bonusSay, bonusHush, bonusCanSpeak } from "./kit/bonusVoice";
import { sfxPool } from "./sfx";
import "./kit/bonusShell.css";
import "./TypingQuiz.css";

const TAP_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";
const CHIME_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";
const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";
const TALK_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";
const MUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmute-button.png?alt=media&token=9b91203e-011f-4324-bbea-300d0d273fcf";
const UNMUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Funmute-button.png?alt=media&token=e6afbbd2-41e5-41c1-b911-513fa41a4360";

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------
const ROUND_SECONDS = 60;
const GROUND_PENALTY = 5;
const STAR_BONUS = 25;
const CLOCK_SECONDS = 5;
const STARS_AT = [80, 180];
const INTRO =
  "Word Blaster! Meteors are falling, and each one has a word on it. Type the word to blast the meteor before it hits the ground. Gold meteors are worth extra points, and blue ones give you more time. Five words in a row makes your points go up!";

const STOP = new Set(
  "the a an and or of to in on at by for with from into onto is are was were be been it its this that these those they them their there here what which who whom whose when where why how not no yes can will would should could has have had do does did than then also very much many more most some any each every all both such only own same so too just about over under after before between because while during".split(
    " "
  )
);
const FALLBACK = [
  "sun", "star", "moon", "rain", "tree", "frog", "book", "fish", "bird", "rock", "leaf", "cloud",
  "plant", "water", "earth", "light", "music", "happy", "smart", "learn", "planet", "rocket",
  "school", "animal", "number", "teacher", "science", "explore",
];

let uidN = 0;
const uid = () => `m${Date.now().toString(36)}${(uidN += 1)}`;

/** The topic's words: from the right answers first, then the questions. */
const buildWords = (questions = []) => {
  const seen = new Set();
  const out = [];
  const add = (text) =>
    String(text || "")
      .toLowerCase()
      .replace(/[’']/g, "")
      .split(/[^a-z]+/)
      .filter((w) => w.length >= 3 && w.length <= 11 && !STOP.has(w))
      .forEach((w) => {
        if (!seen.has(w)) {
          seen.add(w);
          out.push(w);
        }
      });
  (questions || []).forEach((q) => (q?.answers || []).filter((a) => a?.correct).forEach((a) => add(a.text || a.alt)));
  (questions || []).forEach((q) => add(q?.question));
  if (out.length < 10) FALLBACK.forEach((w) => !seen.has(w) && out.push(w));
  return out.sort((a, b) => a.length - b.length);
};

const KEY_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];
const isTouchy = () => {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------

const TypingQuiz = ({ questions = [], placeholderImage, onComplete, onBonusComplete }) => {
  const finishCb = onComplete || onBonusComplete;
  const [phase, setPhase] = useState("intro"); // intro | count | play | done
  const [countdown, setCountdown] = useState(null);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(ROUND_SECONDS);
  const [meteors, setMeteors] = useState([]);
  const [targetId, setTargetId] = useState(null);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [blasted, setBlasted] = useState(0);
  const [letters, setLetters] = useState(0);
  const [pops, setPops] = useState([]);
  const [lasers, setLasers] = useState([]);
  const [booms, setBooms] = useState([]);
  const [flash, setFlash] = useState(null);
  const [shake, setShake] = useState(false);
  const [aim, setAim] = useState(0); // cannon angle, degrees
  const [paused, setPaused] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [showKeys, setShowKeys] = useState(isTouchy);
  const [wrongKey, setWrongKey] = useState(null);

  const words = useRef(buildWords(questions));
  const wordPick = useRef(0);
  const meteorsRef = useRef(meteors);
  const targetRef = useRef(null);
  const scoreRef = useRef(0);
  const comboRef = useRef(0);
  const timerRef = useRef(ROUND_SECONDS);
  const elapsedRef = useRef(0);
  const pausedRef = useRef(false);
  const musicRef = useRef(null);
  const rootRef = useRef(null);
  const cannonRef = useRef(null);
  const sentRef = useRef(false);
  const spawnSoon = useRef(null);
  meteorsRef.current = meteors;
  targetRef.current = targetId;
  scoreRef.current = score;
  comboRef.current = combo;
  timerRef.current = timer;
  pausedRef.current = paused;

  const playTap = useRef(sfxPool(TAP_SOUND_URL)).current;
  const playChime = useRef(sfxPool(CHIME_SOUND_URL, 3)).current;

  // ---- voice + music ----
  const say = useCallback((text) => {
    bonusSay(text, {
      onStart: () => {
        setSpeaking(true);
        if (musicRef.current) musicRef.current.volume = 0.08;
      },
      onEnd: () => {
        setSpeaking(false);
        if (musicRef.current) musicRef.current.volume = 0.28;
      },
    });
  }, []);
  const hush = useCallback(() => {
    bonusHush();
    setSpeaking(false);
    if (musicRef.current) musicRef.current.volume = 0.28;
  }, []);

  useEffect(() => {
    const m = new Audio(MUSIC_URL);
    m.loop = true;
    m.volume = 0.28;
    m.play().catch(() => {});
    musicRef.current = m;
    const t = setTimeout(() => say(INTRO), 150);
    return () => {
      clearTimeout(t);
      m.pause();
      bonusHush();
    };
  }, [say]);

  // ---- flow ----
  const begin = () => {
    hush();
    if (musicRef.current && musicRef.current.paused) musicRef.current.play().catch(() => {});
    setPhase("count");
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown === "GO!") {
      const t = setTimeout(() => {
        setCountdown(null);
        setPhase("play");
      }, 550);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCountdown((c) => (c > 1 ? c - 1 : "GO!")), 700);
    return () => clearTimeout(t);
  }, [countdown]);

  const playing = phase === "play" && !paused;

  // the clock
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      elapsedRef.current += 1;
      setTimer((s) => {
        if (s <= 1) {
          clearInterval(t);
          setPhase("done");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [playing]);

  // next word: short ones first, longer as the round goes on
  const nextWord = () => {
    const list = words.current;
    const progress = Math.min(1, elapsedRef.current / (ROUND_SECONDS * 0.8));
    const maxLen = Math.round(4 + progress * 7); // 4 letters → 11
    const pool = list.filter((w) => w.length <= maxLen);
    const usable = pool.length >= 3 ? pool : list.slice(0, Math.max(3, Math.ceil(list.length / 2)));
    // never two on screen starting with the same letter (so the first
    // letter always picks one meteor)
    const taken = new Set(meteorsRef.current.map((m) => m.word[0]));
    const free = usable.filter((w) => !taken.has(w[0]) && !meteorsRef.current.some((m) => m.word === w));
    const from = free.length ? free : usable;
    wordPick.current += 1 + Math.floor(Math.random() * 3);
    return from[wordPick.current % from.length];
  };

  // the spawner
  useEffect(() => {
    if (!playing) return undefined;
    let t;
    const spawn = () => {
      const e = elapsedRef.current;
      const maxOnScreen = e < 15 ? 3 : e < 35 ? 4 : 5;
      if (meteorsRef.current.length < maxOnScreen) {
        const word = nextWord();
        const r = Math.random();
        const kind = r < 0.12 ? "star" : r < 0.2 && e > 8 && timerRef.current < ROUND_SECONDS - 5 ? "clock" : "rock";
        const speed = Math.max(7, 12 - e * 0.08) + word.length * 0.3;
        // keep clear of the meteors already up there, so words don't overlap
        const lefts = meteorsRef.current.map((x) => x.left);
        let left = 8 + Math.random() * 70;
        for (let tries = 0; tries < 8 && lefts.some((l) => Math.abs(l - left) < 18); tries += 1) left = 8 + Math.random() * 70;
        const m = { id: uid(), word, typed: 0, kind, speed, born: performance.now(), left, drift: (Math.random() - 0.5) * 60, spin: (Math.random() - 0.5) * 16 };
        setMeteors((prev) => [...prev, m]);
      }
      const gap = Math.max(1150, 2300 - e * 22);
      t = setTimeout(spawn, gap);
    };
    t = setTimeout(spawn, meteorsRef.current.length ? 900 : 200);
    // a quick typist can clear the sky: bring the next one straight away
    spawnSoon.current = () => {
      clearTimeout(t);
      t = setTimeout(spawn, 350);
    };
    return () => {
      clearTimeout(t);
      spawnSoon.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // ---- effects ----
  const addPop = (text, xPct, yPx, kind = "") => {
    const id = uid();
    setPops((p) => [...p.slice(-5), { id, text, xPct, yPx, kind }]);
    setTimeout(() => setPops((p) => p.filter((x) => x.id !== id)), 900);
  };
  const showFlash = (text, small = false) => {
    setFlash({ text, small, id: uid() });
    setTimeout(() => setFlash(null), 1100);
  };
  const bump = (ms = 380) => {
    setShake(true);
    setTimeout(() => setShake(false), ms);
  };
  const addScore = (n) => setScore((s) => Math.max(0, s + n));
  const multOf = (c) => 1 + Math.min(2, Math.floor(c / 5));

  const centerOf = (id) => {
    const el = rootRef.current?.querySelector(`[data-mid="${id}"]`);
    const root = rootRef.current?.getBoundingClientRect();
    if (!el || !root) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2 - root.left, y: r.top + r.height / 2 - root.top, w: root.width, h: root.height };
  };
  const cannonTip = () => {
    const el = cannonRef.current;
    const root = rootRef.current?.getBoundingClientRect();
    if (!el || !root) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2 - root.left, y: r.top + r.height * 0.55 - root.top };
  };
  const aimAt = (id) => {
    const c = centerOf(id);
    const tip = cannonTip();
    if (!c || !tip) return;
    setAim((Math.atan2(c.x - tip.x, tip.y - c.y) * 180) / Math.PI);
  };
  const fire = (id, big) => {
    const c = centerOf(id);
    const tip = cannonTip();
    if (!c || !tip) return c;
    const dx = c.x - tip.x;
    const dy = c.y - tip.y;
    const laser = { id: uid(), x: tip.x, y: tip.y, len: Math.hypot(dx, dy), ang: (Math.atan2(dy, dx) * 180) / Math.PI, big };
    setLasers((l) => [...l.slice(-3), laser]);
    setTimeout(() => setLasers((l) => l.filter((x) => x.id !== laser.id)), 260);
    const boom = { id: uid(), x: c.x, y: c.y, big };
    setBooms((b) => [...b.slice(-4), boom]);
    setTimeout(() => setBooms((b) => b.filter((x) => x.id !== boom.id)), 520);
    return c;
  };

  // ---- typing ----
  const miss = (key) => {
    playGameSound("bonusWrong", "typingQuiz", playTap);
    if (comboRef.current > 0) {
      setCombo(0);
      comboRef.current = 0;
    }
    setWrongKey(key);
    setTimeout(() => setWrongKey((k) => (k === key ? null : k)), 220);
  };

  const blast = (m) => {
    const c = fire(m.id, m.kind !== "rock");
    setMeteors((prev) => prev.filter((x) => x.id !== m.id));
    meteorsRef.current = meteorsRef.current.filter((x) => x.id !== m.id);
    if (!meteorsRef.current.length) spawnSoon.current?.();
    setTargetId(null);
    targetRef.current = null;
    const streak = comboRef.current + 1;
    comboRef.current = streak;
    setCombo(streak);
    setBestCombo((b) => Math.max(b, streak));
    setBlasted((n) => n + 1);
    const mult = multOf(streak);
    let pts = (2 + m.word.length) * mult;
    if (m.kind === "star") pts += STAR_BONUS;
    addScore(pts);
    const xPct = c ? (c.x / c.w) * 100 : m.left;
    const yPx = c ? c.y : 200;
    addPop(`+${pts}`, xPct, yPx, m.kind === "star" ? "is-star" : "");
    if (m.kind === "clock") {
      setTimer((s) => s + CLOCK_SECONDS);
      addPop(`⏰ +${CLOCK_SECONDS}s`, xPct, yPx + 46, "is-clock");
    }
    const tierUp = mult > multOf(streak - 1);
    if (tierUp) showFlash(`×${mult} WORD STREAK!`);
    if (m.kind !== "rock" || tierUp) playGameSound("bonusPower", "typingQuiz", playChime);
    else playGameSound("bonusGood", "typingQuiz", playChime);
    if (m.kind === "star" && c) {
      try {
        confetti({ spread: 70, particleCount: 40, startVelocity: 34, colors: ["#ffd43b", "#fff3bf", "#f59f00"], origin: { x: c.x / c.w, y: c.y / c.h } });
      } catch {
        /* ignore */
      }
    }
  };

  const typeKey = (raw) => {
    if (phase !== "play" || pausedRef.current) return;
    const key = String(raw || "").toLowerCase();
    if (!/^[a-z]$/.test(key)) return;
    const list = meteorsRef.current;
    let m = list.find((x) => x.id === targetRef.current);
    if (!m) {
      // lock on to the lowest meteor whose word starts with this letter
      const now = performance.now();
      const fits = list.filter((x) => x.word[0] === key);
      if (!fits.length) {
        miss(key);
        return;
      }
      m = fits.sort((a, b) => (now - b.born) / b.speed - (now - a.born) / a.speed)[0];
      setTargetId(m.id);
      targetRef.current = m.id;
      aimAt(m.id);
      if (m.typed > 0) m = { ...m, typed: 0 };
    }
    if (m.word[m.typed] !== key) {
      miss(key);
      return;
    }
    setLetters((n) => n + 1);
    const typed = m.typed + 1;
    if (typed >= m.word.length) {
      blast({ ...m, typed });
      return;
    }
    playTap();
    const upd = { ...m, typed };
    setMeteors((prev) => prev.map((x) => (x.id === m.id ? upd : x)));
    meteorsRef.current = meteorsRef.current.map((x) => (x.id === m.id ? upd : x));
    aimAt(m.id);
  };
  const typeRef = useRef(typeKey);
  typeRef.current = typeKey;

  const letGo = () => {
    const id = targetRef.current;
    if (!id) return;
    setTargetId(null);
    targetRef.current = null;
    setMeteors((prev) => prev.map((x) => (x.id === id ? { ...x, typed: 0 } : x)));
  };
  const letGoRef = useRef(letGo);
  letGoRef.current = letGo;

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        letGoRef.current();
        return;
      }
      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        typeRef.current(e.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // a meteor reached the ground
  const onGround = useCallback((item) => {
    const still = meteorsRef.current.some((x) => x.id === item.id);
    if (!still) return;
    setMeteors((prev) => prev.filter((x) => x.id !== item.id));
    if (targetRef.current === item.id) {
      setTargetId(null);
      targetRef.current = null;
    }
    setScore((s) => Math.max(0, s - GROUND_PENALTY));
    setCombo(0);
    comboRef.current = 0;
    setShake(true);
    setTimeout(() => setShake(false), 380);
    playGameSound("bonusBomb", "typingQuiz", null);
    setPops((p) => [...p.slice(-5), { id: uid(), text: `💥 -${GROUND_PENALTY}`, xPct: item.left + 6, yPx: null, kind: "is-bad is-ground" }]);
  }, []);
  // tap a meteor to aim at it (handy with the on-screen keyboard)
  const onMeteorTap = useCallback((item) => {
    if (pausedRef.current) return;
    if (targetRef.current && targetRef.current !== item.id) {
      const old = targetRef.current;
      setMeteors((prev) => prev.map((x) => (x.id === old ? { ...x, typed: 0 } : x)));
    }
    setTargetId(item.id);
    targetRef.current = item.id;
  }, []);

  // ---- the end ----
  const send = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    hush();
    finishCb?.(Math.max(0, scoreRef.current), { endedEarly });
  }, [finishCb, endedEarly, hush]);

  useEffect(() => {
    if (phase !== "done") return undefined;
    setMeteors([]);
    setTargetId(null);
    if (scoreRef.current > 0 && !endedEarly) confetti({ spread: 100, particleCount: 120, startVelocity: 50, origin: { y: 0.5 } });
    const t = setTimeout(send, endedEarly ? 4200 : 4000);
    return () => clearTimeout(t);
  }, [phase, endedEarly, send]);

  const endEarly = () => {
    setEndedEarly(true);
    setPhase("done");
  };

  // ---- drawing ----
  const stars = endedEarly ? 0 : score >= STARS_AT[1] ? 3 : score >= STARS_AT[0] ? 2 : score > 0 ? 1 : 0;
  const lowTime = phase === "play" && timer <= 5;
  const mult = multOf(combo);
  const target = meteors.find((m) => m.id === targetId);
  const next = target ? target.word[target.typed] : null;

  return (
    <>
      {phase === "intro" && (
        <div className="bs-overlay">
          <div className="bs-panel">
            <div className="bs-title">⌨️ Typing Quiz: Word Blaster</div>
            <div className="bs-sub">Type the words to blast the meteors!</div>
            <div className="bs-key">
              <div className="bs-key-row">
                <span className="bs-sw">☄️</span>
                <span>Type the word on a meteor to blast it</span>
                <span className="bs-pts">+1 a letter</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">🌟</span>
                <span>Gold meteor — extra points</span>
                <span className="bs-pts">+{STAR_BONUS}</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">⏰</span>
                <span>Blue meteor — more time</span>
                <span className="bs-pts">+{CLOCK_SECONDS}s</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">💥</span>
                <span>Don&apos;t let them hit the ground!</span>
                <span className="bs-pts is-neg">-{GROUND_PENALTY}</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">🔥</span>
                <span>5 words in a row = ×2, 10 = ×3</span>
              </div>
            </div>
            <div className="bs-actions">
              {bonusCanSpeak() && (
                <button type="button" className="bs-talk" onClick={() => say(INTRO)} title="Read the directions" aria-label="Read the directions out loud">
                  <img src={TALK_URL} alt="Talk text" />
                </button>
              )}
              {speaking && (
                <button type="button" className="bs-stop" onClick={hush}>
                  ⏹ Stop
                </button>
              )}
              <button type="button" className="bs-start" onClick={begin}>
                Start!
              </button>
            </div>
          </div>
        </div>
      )}

      {countdown !== null && (
        <div className="bs-count">
          <span key={String(countdown)}>{countdown}</span>
        </div>
      )}

      <div
        ref={rootRef}
        className={`bs-root wb-root ${shake ? "is-shake" : ""} ${showKeys ? "has-keys" : ""}`}
        style={placeholderImage ? { backgroundImage: `url('${placeholderImage}')` } : undefined}
      >
        <div className="wb-stars" aria-hidden="true" />
        <img src={LOGO_URL} alt="SBL Logo" className="bs-logo" />

        <div className="bs-hud">
          <div className="bs-card">
            <span className="bs-label">Score</span>
            <div className="bs-value">{score}</div>
          </div>
          <div className="bs-card" style={{ textAlign: "right" }}>
            <span className="bs-label">Time</span>
            <div className={`bs-value ${lowTime ? "is-urgent" : ""}`}>{timer}s</div>
            <div className="bs-track">
              <div className={`bs-fill ${lowTime ? "is-urgent" : ""}`} style={{ width: `${Math.min(100, (timer / ROUND_SECONDS) * 100)}%` }} />
            </div>
          </div>
        </div>

        {phase === "play" && combo > 0 && (
          <div className="wb-streak">
            🔥 {combo} in a row {mult > 1 && <b>×{mult}</b>}
          </div>
        )}

        {flash && (
          <div key={flash.id} className={`bs-flash ${flash.small ? "is-small" : ""}`}>
            {flash.text}
          </div>
        )}

        {phase === "play" &&
          meteors.map((m) => (
            <Faller
              key={m.id}
              item={m}
              data-mid={m.id}
              className={`wb-meteor is-${m.kind} ${m.id === targetId ? "is-target" : ""}`}
              style={{ left: `${m.left}%`, top: -120 }}
              speed={m.speed}
              drift={m.drift}
              spin={m.spin}
              distance={showKeys ? "calc(100vh - 250px)" : "calc(100vh - 70px)"}
              paused={paused}
              onDone={onGround}
              onTap={onMeteorTap}
            >
              <div className="wb-rock" aria-hidden="true">
                {m.kind === "star" ? "🌟" : m.kind === "clock" ? "⏰" : ""}
              </div>
              <div className="wb-word" aria-label={m.word}>
                {m.word.split("").map((ch, i) => (
                  <span key={i} className={i < m.typed ? "is-done" : i === m.typed && m.id === targetId ? "is-next" : ""}>
                    {ch}
                  </span>
                ))}
              </div>
            </Faller>
          ))}

        {lasers.map((l) => (
          <div
            key={l.id}
            className={`wb-laser ${l.big ? "is-big" : ""}`}
            style={{ left: l.x, top: l.y, width: l.len, transform: `rotate(${l.ang}deg)` }}
          />
        ))}
        {booms.map((b) => (
          <div key={b.id} className={`wb-boom ${b.big ? "is-big" : ""}`} style={{ left: b.x, top: b.y }} />
        ))}
        {pops.map((p) => (
          <div key={p.id} className={`bs-pop wb-pop ${p.kind}`} style={{ left: `${p.xPct}%`, top: p.yPx ?? undefined }}>
            {p.text}
          </div>
        ))}

        {/* the ground and the cannon */}
        <div className="wb-ground" />
        <div className="wb-cannon" ref={cannonRef}>
          <div className="wb-barrel" style={{ transform: `rotate(${aim}deg)` }} />
          <div className="wb-base" />
        </div>
        {target && (
          <div className="wb-typing">
            <span className="wb-done">{target.word.slice(0, target.typed)}</span>
            <span className="wb-rest">{target.word.slice(target.typed)}</span>
          </div>
        )}

        {/* the on-screen keyboard */}
        {showKeys && phase !== "intro" && (
          <div className="wb-keys" role="group" aria-label="Keyboard">
            {KEY_ROWS.map((row) => (
              <div className="wb-row" key={row}>
                {row.split("").map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`wb-key ${next === k ? "is-next" : ""} ${wrongKey === k ? "is-wrong" : ""}`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      typeKey(k);
                    }}
                  >
                    {k}
                  </button>
                ))}
                {row === "zxcvbnm" && (
                  <button
                    type="button"
                    className="wb-key is-wide"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      letGo();
                    }}
                    aria-label="Let go of this meteor"
                  >
                    ↺
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <button type="button" className="wb-keys-toggle" onClick={() => setShowKeys((v) => !v)} aria-label={showKeys ? "Hide the keyboard" : "Show the keyboard"}>
          ⌨️
        </button>
        <button
          type="button"
          className="bs-mute"
          onClick={() => {
            const n = !muted;
            setMuted(n);
            if (musicRef.current) musicRef.current.muted = n;
          }}
          aria-label={muted ? "Turn music on" : "Turn music off"}
        >
          <img src={muted ? UNMUTE_URL : MUTE_URL} alt="" />
        </button>
      </div>

      {(phase === "play" || phase === "count") && <BonusEndEarly onEnd={endEarly} onPause={setPaused} />}

      {phase === "done" && (
        <div className="bs-overlay is-summary">
          <div className="bs-panel">
            <div className="bs-title">{endedEarly ? "Round ended early" : "Bonus Round Complete!"}</div>
            {!endedEarly && (
              <div className="bs-stars" aria-label={`${stars} out of 3 stars`}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`bs-star ${i < stars ? "" : "is-dim"}`}>
                    ⭐
                  </span>
                ))}
              </div>
            )}
            <div className="bs-score-label">Bonus Points</div>
            <div className="bs-score">{score}</div>
            <div className="bs-stats">
              <div className="bs-stat">
                <div className="n">{blasted}</div>
                <div className="k">Words blasted</div>
              </div>
              <div className="bs-stat">
                <div className="n">{letters}</div>
                <div className="k">Letters typed</div>
              </div>
              <div className="bs-stat">
                <div className="n">{bestCombo}</div>
                <div className="k">Best streak</div>
              </div>
            </div>
            {endedEarly && <p className="bs-early-note">Your points still count! Play the whole bonus round next time to earn the ⭐ star.</p>}
            <button type="button" className="bs-continue" onClick={send}>
              Continue ▶
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TypingQuiz;
